// 📄 pages/Legal/PrivacidadExtension.tsx
// /privacidad-extension: política de privacidad de la extensión de Chrome
// "OneHub Soporte". Pública (sin sesión): la Chrome Web Store exige una URL
// que cualquiera pueda abrir, y la extensión la enlaza desde el popup y
// Opciones. Esta página es la fuente del texto (ver
// docs/legal/politica-privacidad-extension.md).
//
// Si cambia lo que la extensión lee o envía (chrome-extension/src/), esta
// página tiene que cambiar con ella.
import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';

const ACTUALIZADA = '3 de noviembre de 2026';
const CONTACTO = 'contacto@getonehub.com';

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-white">{titulo}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-gray-300">{children}</div>
    </section>
  );
}

const Lista = ({ children }: { children: React.ReactNode }) => <ul className="list-disc pl-5 space-y-1.5">{children}</ul>;
const Correo = () => <a href={`mailto:${CONTACTO}`} className="text-blue-400 hover:underline">{CONTACTO}</a>;

export default function PrivacidadExtension() {
  return (
    <div className="min-h-screen bg-gray-900 text-white px-4 py-10">
      <article className="max-w-3xl mx-auto space-y-8">
        <header className="space-y-2">
          <p className="flex items-center gap-2 text-sm text-blue-400">
            <ShieldCheck className="w-4 h-4" /> OneHub
          </p>
          <h1 className="text-2xl font-bold">Política de privacidad de la extensión "OneHub Soporte"</h1>
          <p className="text-sm text-gray-400">Última actualización: {ACTUALIZADA}</p>
        </header>

        <p className="text-sm leading-relaxed text-gray-300">
          Esta política explica qué información usa la extensión de Chrome <strong>OneHub Soporte</strong> (la "extensión"), para qué la usa y
          con quién la comparte. El responsable del tratamiento es <strong>OneHub</strong> (empresa en proceso de constitución), con domicilio en
          Villavicencio, Meta, Colombia, y correo de contacto <Correo />. Se aplica la ley colombiana de protección de datos personales
          (Ley 1581 de 2012 y sus decretos reglamentarios).
        </p>

        <Seccion titulo="1. Para qué sirve la extensión">
          <p>
            La extensión ayuda a los usuarios de OneHub a resolver problemas con documentos de <strong>SAP Business Network</strong>, por ejemplo una
            factura rechazada o fallida:
          </p>
          <Lista>
            <li>lee el estatus y el historial del documento que tienes abierto;</li>
            <li>lo compara con un catálogo de errores conocidos y muestra la causa y la solución;</li>
            <li>si lo pides, genera una explicación con inteligencia artificial;</li>
            <li>si el problema no se resuelve, crea un ticket para el equipo de soporte de tu proyecto, con un reporte del documento;</li>
            <li>al personal de soporte autorizado le permite, además, enviar el estatus de una factura al sistema del cliente.</li>
          </Lista>
          <p>La extensión solo funciona con una cuenta de OneHub y solo se activa en páginas de SAP Business Network (<code>*.bn.cloud.ariba.com</code>).</p>
        </Seccion>

        <Seccion titulo="2. Qué información usa">
          <p><strong>Datos de tu cuenta de OneHub.</strong></p>
          <Lista>
            <li>
              <strong>Correo y contraseña</strong>, solo al iniciar sesión desde la ventana de la extensión. La contraseña va directamente al
              servicio de autenticación (Google Firebase Authentication); la extensión no la guarda.
            </li>
            <li><strong>Sesión:</strong> el servicio de autenticación deja una sesión guardada en tu navegador, que la extensión usa solo para identificarte ante OneHub.</li>
          </Lista>
          <p>
            <strong>Información de la página de SAP Business Network.</strong> La extensión solo <em>lee</em> la página que tienes abierta: no
            hace clic por ti, no usa tu sesión ni tus cookies de SAP y no se comunica con SAP. Lee únicamente:
          </p>
          <Lista>
            <li>el número, el estatus y el historial de eventos del documento abierto. Cada evento tiene código, texto, fecha y quién lo generó, y puede contener nombres de usuarios o sistemas;</li>
            <li>el ANID (identificador de tu cuenta de Business Network), solo cuando abres el menú de tu perfil, para saber a qué cliente de OneHub pertenece la cuenta;</li>
            <li>si la página está en modo de prueba o de producción;</li>
            <li>la dirección de la página, solo si creas un ticket, para que el equipo de soporte pueda abrir el mismo documento.</li>
          </Lista>
          <p><strong>Lo que tú escribes:</strong> la descripción y la prioridad de un ticket y los mensajes de su conversación. Si eres personal de soporte y envías un estatus, también el ID de proveedor, el estatus elegido y el comentario.</p>
          <p><strong>Lo que la extensión guarda en tu navegador:</strong> solo el entorno de OneHub elegido en Opciones, una marca de tiempo para saber cuándo cambió la sesión y la sesión descrita arriba. No guarda el contenido de los documentos.</p>
        </Seccion>

        <Seccion titulo="3. Para qué se usa">
          <Lista>
            <li>Identificarte y comprobar que perteneces al cliente dueño de la cuenta de Business Network.</li>
            <li>Diagnosticar el documento contra el catálogo de errores conocidos.</li>
            <li>Generar la explicación con inteligencia artificial, solo cuando la pides.</li>
            <li>Crear y atender tus tickets y avisar por correo a las personas involucradas.</li>
            <li>Enviar el estatus de una factura al sistema del cliente, solo si lo hace personal de soporte autorizado.</li>
            <li>Seguridad y auditoría: registramos quién hizo cada acción, cuándo y sobre qué documento.</li>
          </Lista>
          <p>
            <strong>No</strong> usamos esta información para publicidad, <strong>no</strong> la vendemos y <strong>no</strong> la usamos para
            evaluar créditos ni con ningún fin ajeno al soporte descrito.
          </p>
        </Seccion>

        <Seccion titulo="4. Con quién se comparte">
          <Lista>
            <li><strong>OneHub:</strong> la información viaja cifrada (HTTPS) a los servidores de OneHub en Google Cloud y Firebase.</li>
            <li><strong>Equipo de soporte de tu proyecto:</strong> ve los tickets, sus reportes y la conversación. Si eres usuario de un cliente, también ven tus tickets las demás personas de tu empresa en ese proyecto.</li>
            <li>
              <strong>Proveedores de inteligencia artificial</strong>, solo cuando pides la explicación: se envían el estatus y el código y el texto
              de los eventos a Google (Gemini) y, si este no responde, a Anthropic (Claude). <strong>No se envían</strong> el número del
              documento, el ANID ni quién generó cada evento.
            </li>
            <li><strong>Sistema del cliente:</strong> el envío de estatus va a la integración del propio cliente con su ERP (SAP Cloud Integration), y solo lo hace personal de soporte autorizado.</li>
            <li><strong>Correo electrónico:</strong> los avisos de tickets se envían al correo de las personas involucradas.</li>
          </Lista>
          <p>No compartimos la información con nadie más, salvo obligación legal.</p>
        </Seccion>

        <Seccion titulo="5. Dónde se guarda">
          <p>
            Los servidores de OneHub (Google Cloud y Firebase) y los proveedores de inteligencia artificial están en <strong>Estados Unidos</strong>.
            Al usar la extensión, tu información se transfiere a ese país, con las medidas de seguridad de la sección 7.
          </p>
        </Seccion>

        <Seccion titulo="6. Cuánto tiempo se conserva">
          <Lista>
            <li><strong>Tickets, conversaciones y reportes:</strong> mientras el proyecto esté activo y hasta 12 meses después de su cierre.</li>
            <li><strong>Registros de auditoría:</strong> 24 meses.</li>
            <li><strong>Explicaciones con inteligencia artificial:</strong> no se guardan, salvo la que adjuntes a un ticket.</li>
            <li><strong>Tu cuenta:</strong> mientras tengas acceso a OneHub o hasta que pidas su eliminación.</li>
            <li><strong>En tu navegador:</strong> hasta que cierres sesión o desinstales la extensión.</li>
          </Lista>
          <p>Cumplidos esos plazos, la información se elimina o se anonimiza.</p>
        </Seccion>

        <Seccion titulo="7. Seguridad">
          <Lista>
            <li>Toda la comunicación usa HTTPS.</li>
            <li>La sesión nunca se expone a la página de SAP: solo la usa la parte interna de la extensión.</li>
            <li>La extensión solo envía a OneHub los datos necesarios para cada función.</li>
            <li>Los servidores de OneHub validan el acceso de cada usuario, no la extensión.</li>
          </Lista>
        </Seccion>

        <Seccion titulo="8. Tus derechos">
          <p>Como titular de los datos tienes derecho a:</p>
          <Lista>
            <li>conocer, actualizar y rectificar tus datos;</li>
            <li>pedir prueba de la autorización que nos diste;</li>
            <li>saber qué uso les damos;</li>
            <li>revocar la autorización o pedir que se eliminen, cuando no exista un deber legal o contractual de conservarlos;</li>
            <li>acceder gratis a tus datos;</li>
            <li>presentar quejas ante la Superintendencia de Industria y Comercio (SIC).</li>
          </Lista>
          <p>
            Para ejercerlos, escríbenos a <Correo /> indicando tu nombre, tu correo de OneHub y lo que solicitas. Respondemos las consultas en un
            máximo de 10 días hábiles y los reclamos en un máximo de 15 días hábiles, como indica la ley.
          </p>
          <p>
            También puedes cerrar sesión desde la ventana de la extensión y desinstalarla en cualquier momento. La explicación con inteligencia
            artificial y los tickets son siempre opcionales.
          </p>
        </Seccion>

        <Seccion titulo="9. Autorización">
          <p>
            Al iniciar sesión en la extensión aceptas esta política y autorizas el tratamiento de tus datos para las finalidades descritas. Si
            compartes en un ticket datos de otras personas, debes contar con su autorización.
          </p>
        </Seccion>

        <Seccion titulo="10. Cambios a esta política">
          <p>Si la cambiamos, publicaremos la nueva versión en esta misma dirección con su fecha de actualización.</p>
        </Seccion>

        <Seccion titulo="11. Contacto">
          <p>OneHub — Villavicencio, Meta, Colombia — <Correo /></p>
        </Seccion>

        <footer className="pt-4 border-t border-white/10 text-sm">
          <Link to="/" className="text-blue-400 hover:underline">Ir a OneHub</Link>
        </footer>
      </article>
    </div>
  );
}
